// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/main.ts
// Rôle : monte l'application et son routage.
// Référence : CIG-ARCHITECTURE.md §2.

import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';
import '@cig/design-system/tokens.css';
import App from './App.vue';
import { useNavigation } from './lib/navigation.ts';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: () => import('./explorer/Explorateur.vue') },
    { path: '/explorer/:nodeId', component: () => import('./explorer/Explorateur.vue') },
    // Les vues transverses sont servies par l'explorateur lui-meme : seule la
    // racine du fil d'Ariane et la source des entrees changent. Un utilisateur
    // n'a pas a reapprendre son explorateur d'une vue a l'autre.
    {
      path: '/favoris',
      component: () => import('./explorer/Explorateur.vue'),
      meta: { vue: 'favorites' },
    },
    {
      path: '/recents',
      component: () => import('./explorer/Explorateur.vue'),
      meta: { vue: 'recent' },
    },
    {
      path: '/partage-avec-moi',
      component: () => import('./explorer/Explorateur.vue'),
      meta: { vue: 'shared' },
    },
    {
      path: '/corbeille',
      component: () => import('./explorer/Explorateur.vue'),
      meta: { vue: 'trashed' },
    },
    { path: '/jetons', component: () => import('./explorer/Jetons.vue') },
    { path: '/collections', component: () => import('./explorer/Collections.vue') },
    { path: '/modeles', component: () => import('./explorer/Modeles.vue') },
    { path: '/circuits', component: () => import('./workflows/Circuits.vue') },
    { path: '/workflows/:id', component: () => import('./workflows/Circuit.vue') },
    { path: '/partages', component: () => import('./share/Partages.vue') },
    // Les revues d'accès sont adressées au propriétaire métier, pas à l'IT :
    // elles vivent dans la navigation ordinaire (spécification §18.8).
    { path: '/revues', component: () => import('./governance/Revues.vue') },
    { path: '/revues/:id', component: () => import('./governance/Revues.vue') },
    { path: '/notifications', component: () => import('./explorer/Notifications.vue') },
    { path: '/documentation', component: () => import('./docs/Documentation.vue') },
    { path: '/admin', component: () => import('./admin/Console.vue') },
    { path: '/:reste(.*)*', component: () => import('./Introuvable.vue') },
  ],
  scrollBehavior: () => ({ top: 0 }),
});

const pinia = createPinia();

/**
 * Suivi de navigation. Le routeur charge lui-même le composant de la route
 * avant de la confirmer : à `afterEach`, le module est en mémoire. Reste le
 * montage, puis les chargements que la vue déclare ; l'attente ne se lève
 * qu'ensuite. L'utilisateur ne voit donc jamais un écran vide se remplir.
 */
router.beforeEach((vers, depuis) => {
  // La toute première navigation part d'un emplacement sans route : il n'y a
  // alors aucun écran à remplacer, donc rien à voiler.
  useNavigation(pinia).demarrer(vers.path, depuis.matched.length === 0);
});

router.afterEach(() => {
  // Le montage a lieu au tour suivant : attendre ici laisse la vue déclarer
  // ses chargements avant que l'attente ne soit levée.
  void nextTick(() => useNavigation(pinia).routeArrivee());
});

createApp(App).use(pinia).use(router).mount('#app');
